import { Box, Typography } from '@mui/material'
import { MRT_Row } from 'material-react-table'

import { RelationDto, RelationStatus } from '@/rtkApi/mddApiSliceGeneratedV2'

const CONNECTOR_COLOR = '#c9ced6'

interface RelationNameCellProps {
	row: MRT_Row<RelationDto>
}

const LEVEL_STEP = 18

const columnPair = (relation: RelationDto) => {
	const left = relation.leftSideColumn?.code
	const right = relation.rightSideColumn?.code
	if (!left && !right) return ''
	return [left, right].filter(Boolean).join(' → ')
}

export const RelationNameCell = ({ row }: RelationNameCellProps) => {
	const depth = row.depth
	const isChild = depth > 0
	const childCount = row.subRows?.length ?? 0
	const isLastChild =
		isChild && row.getParentRow()?.subRows?.at(-1)?.id === row.id
	const railLeft = (depth - 1) * LEVEL_STEP + 4
	const columns = columnPair(row.original)

	return (
		<Box
			sx={{
				alignItems: 'center',
				display: 'flex',
				minHeight: 32,
				minWidth: 0,
				pl: isChild ? `${depth * LEVEL_STEP + 10}px` : 0,
				position: 'relative',
			}}
		>
			{isChild && (
				<>
					<Box
						sx={{
							borderLeft: `1px solid ${CONNECTOR_COLOR}`,
							bottom: isLastChild ? '50%' : -8,
							left: railLeft,
							position: 'absolute',
							top: -8,
						}}
					/>
					<Box
						sx={{
							borderTop: `1px solid ${CONNECTOR_COLOR}`,
							left: railLeft,
							position: 'absolute',
							top: '50%',
							width: 12,
						}}
					/>
				</>
			)}

			<Typography
				component="span"
				noWrap
				sx={{ fontSize: 14, fontWeight: childCount > 0 ? 600 : 400 }}
			>
				{row.original.name || '—'}
			</Typography>

			{columns && (
				<Typography
					component="span"
					noWrap
					sx={{ color: 'text.secondary', fontSize: 12, ml: 1 }}
				>
					{columns}
				</Typography>
			)}

			{childCount > 0 && (
				<Typography
					component="span"
					sx={{ color: 'text.secondary', fontSize: 12, ml: 1 }}
				>
					({childCount})
				</Typography>
			)}
		</Box>
	)
}

export const RelationTypeCell = ({ label }: { label?: string }) =>
	label ? (
		<Typography component="span" sx={{ color: 'text.secondary', fontSize: 13 }}>
			{label}
		</Typography>
	) : null

const statusDotColor: Record<RelationStatus, string> = {
	BROKEN: '#d32f2f',
	FK_COL_NOT_SET: '#ed6c02',
	OK: '#2e7d32',
	PK_COL_REMOVED: '#ed6c02',
	PK_SIDE_REMOVED: '#d32f2f',
}

interface RelationStatusCellProps {
	label: string
	status?: RelationStatus
}

export const RelationStatusCell = ({
	label,
	status,
}: RelationStatusCellProps) => {
	if (!status) return null

	return (
		<Box sx={{ alignItems: 'center', display: 'flex', gap: 0.75 }}>
			<Box
				sx={{
					backgroundColor: statusDotColor[status],
					borderRadius: '50%',
					flex: '0 0 auto',
					height: 7,
					width: 7,
				}}
			/>
			<Typography component="span" sx={{ fontSize: 13 }}>
				{label}
			</Typography>
		</Box>
	)
}
